/**
 * JITAgentConfigModal — Just-In-Time agent config (T9/B 2026-05-27).
 *
 * Wzorzec: zamiast forced upfront OnboardingWizard (7-step) zbierającego
 * wszystkie dane na raz, prosimy user'a o KONKRETNĄ konfigurację dopiero
 * gdy chce użyć feature który tego wymaga. UX-driven, nie config-driven.
 *
 * Trzy use cases:
 *   - feature="email"  → wymaga agent.identity.email
 *                        (np. "Wyślij email do dostawcy" w RFQ)
 *   - feature="phone"  → wymaga agent.identity.phone + (opcjonalnie) Twilio
 *                        (np. "Zadzwoń do kontrahenta" w PhoneCalls)
 *   - feature="voice"  → wymaga agent.identity.voice_id wybranego świadomie
 *                        (np. "Posłuchaj jak brzmi Adam" w Settings)
 *
 * Usage:
 *   import { useAgentConfigGate } from './JITAgentConfigModal';
 *
 *   const { needsConfig, openConfig, ConfigModal } = useAgentConfigGate('email');
 *
 *   async function handleSendEmail() {
 *     if (needsConfig) {
 *       openConfig(() => actuallySendEmail());  // run callback after user fills
 *       return;
 *     }
 *     actuallySendEmail();
 *   }
 *
 *   return (<>
 *     <Button onClick={handleSendEmail}>Wyślij</Button>
 *     <ConfigModal />
 *   </>);
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Mail, Phone, Mic, Loader2, Check, Info } from 'lucide-react';
import { getAgentConfig, updateAgentConfig } from '../../lib/api';

const VOICES = [
  { id: 'onyx',    name: 'Onyx',    desc: 'Autorytarny, męski' },
  { id: 'echo',    name: 'Echo',    desc: 'Ciepły, męski' },
  { id: 'nova',    name: 'Nova',    desc: 'Energiczny, kobiecy' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Delikatny, kobiecy' },
  { id: 'alloy',   name: 'Alloy',   desc: 'Neutralny' },
  { id: 'fable',   name: 'Fable',   desc: 'Spokojny' },
];

const FEATURE_META = {
  email: {
    icon: Mail,
    title: 'Adam potrzebuje adresu email',
    description:
      'Żeby Adam mógł wysłać email w Twoim imieniu (do dostawcy / klienta), potrzebuje własnego adresu. Domyślnie sugerujemy adres MASKIL, możesz wpisać własną domenę.',
    field: 'email',
    label: 'Email agenta',
    placeholder: 'adam@twoja-firma.pl',
    type: 'email',
    suggestedFn: (user) => {
      if (!user?.email) return 'adam@maskil.pl';
      // Dla user@firma.com → adam@firma.com
      const parts = user.email.split('@');
      if (parts.length === 2) return `adam@${parts[1]}`;
      return 'adam@maskil.pl';
    },
    helpText:
      'Adam wyśle z tego adresu. Reply-To może być inny (np. Twój własny) — ustawisz w Ustawieniach.',
  },
  phone: {
    icon: Phone,
    title: 'Adam potrzebuje numeru telefonu',
    description:
      'Żeby Adam mógł zadzwonić do kontrahenta (przez Twilio), potrzebuje numeru z którego dzwonić. Numer musi być potwierdzony w panelu Twilio.',
    field: 'phone',
    label: 'Numer telefonu agenta',
    placeholder: '+48 123 456 789',
    type: 'tel',
    suggestedFn: () => '',
    helpText:
      'Format międzynarodowy z +48. Twilio musi być skonfigurowane oddzielnie — sprawdź Ustawienia → Twilio.',
  },
  voice: {
    icon: Mic,
    title: 'Wybierz głos Adama',
    description:
      'Adam mówi przez TTS gdy dzwoni do kontrahenta. Wybierz głos który najlepiej pasuje do Twojej marki.',
    field: 'voice_id',
    label: 'Głos',
    type: 'voice-select',
    suggestedFn: () => 'onyx',
    helpText: 'Domyślnie Onyx (autorytarny męski). Możesz zmienić w Ustawieniach.',
  },
};

/**
 * Hook — sprawdza czy feature potrzebuje konfiguracji + dostarcza
 * openConfig() function + <ConfigModal /> component do renderu w drzewie.
 */
export function useAgentConfigGate(feature) {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [onSuccessCb, setOnSuccessCb] = useState(null);

  // Cache config — pojedynczy fetch przy mount
  useEffect(() => {
    let cancelled = false;
    getAgentConfig().then((c) => {
      if (!cancelled) setConfig(c);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const meta = FEATURE_META[feature];
  const fieldValue = config?.identity?.[meta?.field];
  const needsConfig = !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '');

  const openConfig = useCallback((onSuccess) => {
    setOnSuccessCb(() => onSuccess);
    setOpen(true);
  }, []);

  const handleSaved = useCallback((newConfig) => {
    setConfig(newConfig);
    setOpen(false);
    if (typeof onSuccessCb === 'function') {
      // Defer — żeby dialog zdążył się zamknąć przed user action
      setTimeout(() => onSuccessCb(), 50);
    }
    setOnSuccessCb(null);
  }, [onSuccessCb]);

  const ConfigModal = useCallback(() => (
    <JITConfigDialog
      feature={feature}
      open={open}
      onOpenChange={setOpen}
      currentConfig={config}
      onSaved={handleSaved}
    />
  ), [feature, open, config, handleSaved]);

  return { needsConfig, openConfig, ConfigModal, config };
}


function JITConfigDialog({ feature, open, onOpenChange, currentConfig, onSaved }) {
  const meta = FEATURE_META[feature];
  const Icon = meta?.icon || Mail;
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Pre-fill from suggestedFn when dialog opens
  useEffect(() => {
    if (!open) return;
    const initial = currentConfig?.identity?.[meta.field];
    if (initial) {
      setValue(initial);
    } else {
      // Suggested default — może użyć user.email dla auto-domain
      let user = null;
      try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch {}
      setValue(meta.suggestedFn(user));
    }
  }, [open, currentConfig, meta]);

  async function handleSave() {
    if (meta.field !== 'voice_id') {
      const trimmed = value.trim();
      if (!trimmed) {
        toast.error('Wpisz wartość — pole jest wymagane');
        return;
      }
      if (meta.field === 'email' && !trimmed.includes('@')) {
        toast.error('Niepoprawny adres email');
        return;
      }
      if (meta.field === 'phone' && !trimmed.match(/^[+0-9\s-()]{6,}$/)) {
        toast.error('Niepoprawny numer telefonu');
        return;
      }
    }
    setSaving(true);
    try {
      const updated = {
        ...(currentConfig || {}),
        identity: {
          ...(currentConfig?.identity || {}),
          [meta.field]: value,
        },
      };
      await updateAgentConfig(updated);
      toast.success(`Zapisano: ${meta.label}`);
      onSaved(updated);
    } catch (err) {
      toast.error('Nie udało się zapisać: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }

  if (!meta) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Icon className="w-5 h-5" />
            </div>
            {meta.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">{meta.description}</p>

          {meta.type === 'voice-select' ? (
            <div className="grid grid-cols-2 gap-2">
              {VOICES.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setValue(v.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    value === v.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{v.name}</span>
                    {value === v.id && <Check className="w-3.5 h-3.5 text-primary" />}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{v.desc}</p>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <Label className="text-xs">{meta.label}</Label>
              <Input
                type={meta.type}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={meta.placeholder}
                className="mt-1.5"
                autoFocus
              />
            </div>
          )}

          {meta.helpText && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{meta.helpText}</span>
            </div>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-border">
            <span className="text-[11px] text-muted-foreground">
              Możesz zmienić to później w <Badge variant="outline" className="text-[10px] py-0">Ustawienia</Badge>
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
                Anuluj
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Zapisz i kontynuuj
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
